import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from './types';

/**
 * Resolve the UI locale at boot. Priority:
 *
 *  1. Explicit hint from the embedding plugin (admin preview passes its
 *     own locale; storefront snippets can do the same later).
 *  2. `navigator.language` (browser default).
 *  3. {@link DEFAULT_LOCALE} (`en_US`).
 *
 * Hints like `de`, `DE`, `de-DE`, `de_DE`, `en-GB` are all normalised to the
 * matching supported locale (e.g. `en-GB` → `en_US` — closest English variant).
 */
export function resolveLocale(hint: string | undefined): Locale {
    const fromHint = matchLocale(hint);
    if (fromHint) return fromHint;

    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
        const fromBrowser = matchLocale(navigator.language);
        if (fromBrowser) return fromBrowser;
        // navigator.languages — preferred list, may contain a better match
        const list = (navigator as Navigator & { languages?: ReadonlyArray<string> }).languages;
        if (Array.isArray(list)) {
            for (const candidate of list) {
                const matched = matchLocale(candidate);
                if (matched) return matched;
            }
        }
    }

    return DEFAULT_LOCALE;
}

function matchLocale(raw: string | undefined): Locale | undefined {
    if (!raw) return undefined;
    // Normalise separators: 'de-DE' / 'de_DE' / 'de.DE' → 'de_de' (lower)
    const normalised = raw.replace(/[-.]/g, '_').toLowerCase();
    // Direct match by full code
    for (const supported of SUPPORTED_LOCALES) {
        if (supported.toLowerCase() === normalised) return supported;
    }
    // Match by language portion only
    const lang = normalised.split('_')[0];
    for (const supported of SUPPORTED_LOCALES) {
        if (supported.toLowerCase().startsWith(lang + '_')) return supported;
    }
    return undefined;
}
